/**
 * BioMax PaCC — reconciled liveness status (CD tidy-up 2026-06-14, target E).
 *
 * THE PROBLEM the CD review caught: three different "live" counts on one
 * screen — the header pill (live sourceHealth, denominator = total feeds),
 * the INVESTIGATIONAL ribbon (liveness snapshot, denominator = reportable =
 * total − staged), and the footer (15 pathogens — a different noun entirely).
 * The 79 vs 56 denominators didn't reconcile, and the SAME feed health
 * rendered red in the pill but green in the ribbon.
 *
 * THE FIX: ONE vocabulary, ONE denominator, ONE data source. Both the pill and
 * the ribbon read from the SAME committed liveness snapshot (LIVENESS), so the
 * breakdown (live + stale + staged) sums to the total BY CONSTRUCTION. "stale"
 * folds the snapshot's `aging` + `down` (both = "not flowing on cadence right
 * now", honest either way); "staged" = not-yet-live-by-design.
 *
 * The status DOT encodes ATTENTION — is any supposed-to-be-live feed actually
 * DOWN (a genuine fault), not the raw live/total ratio. So a pile of
 * expected-periodic reference feeds that read as "stale" no longer paints a
 * false emergency. This mirrors the CD verdict in tidyup-status.jsx.
 *
 * NO FABRICATED NUMBERS: every field is read straight off the committed
 * snapshot the 6-hourly cron produces. When no snapshot has run yet (LIVENESS
 * is null) we return a `pending` status and say so plainly — never a seeded
 * "all live" headline.
 */

import type { LivenessSnapshot } from '@biomax/plugin-surveillance-feed';
import { LIVENESS } from './catalog';

/** The single honest liveness vocabulary. One denominator (`total` feeds). */
export interface LivenessStatusView {
  /** True once a scheduled ingest has produced a snapshot. */
  ready: boolean;
  /** Genuinely flowing fresh data right now — the headline number. */
  live: number;
  /** Reachable-but-late (`aging`) + genuine faults (`down`), folded into the
   *  one honest "not on cadence right now" bucket. `down` is broken out
   *  separately for the attention dot. */
  stale: number;
  /** Of `stale`, the genuine faults (a supposed-to-be-live feed failed). */
  down: number;
  /** Not-yet-live-by-design (gate-blocked OR wire-ready). Not a failure. */
  staged: number;
  /** The single denominator: live + stale + staged === total, by construction. */
  total: number;
  /** ISO timestamp the snapshot was produced (null when pending). */
  generatedAt: string | null;
  /**
   * The attention level the status DOT encodes — NOT the raw ratio.
   *   'down'   — at least one supposed-to-be-live feed is faulting (act on it)
   *   'stale'  — nothing down, but some feeds are past cadence (watch)
   *   'ok'     — everything supposed-to-be-live is flowing
   *   'pending'— no scheduled ingest has run yet
   */
  attention: 'ok' | 'stale' | 'down' | 'pending';
}

/** Map an attention level to the existing feedhealth state token. */
export const ATTENTION_COLOR: Record<LivenessStatusView['attention'], string> = {
  ok: 'var(--bm-state-live)',
  stale: 'var(--bm-state-stale)',
  down: 'var(--bm-state-degraded)',
  pending: 'var(--bm-text-subtle)',
};

/** Derive the reconciled view from a snapshot (pure — testable without React). */
export function deriveLivenessStatus(snap: LivenessSnapshot | null): LivenessStatusView {
  if (!snap) {
    return { ready: false, live: 0, stale: 0, down: 0, staged: 0, total: 0, generatedAt: null, attention: 'pending' };
  }
  const stale = snap.aging + snap.down;
  const attention: LivenessStatusView['attention'] =
    snap.down > 0 ? 'down' : stale > 0 ? 'stale' : 'ok';
  return {
    ready: true,
    live: snap.live,
    stale,
    down: snap.down,
    staged: snap.staged,
    total: snap.total,
    generatedAt: snap.generatedAt,
    attention,
  };
}

/** The app-wide reconciled liveness status, read once from the committed
 *  snapshot. The pill, the ribbon, and any other "how live are we" readout
 *  MUST source from here so they can never diverge again. */
export const LIVENESS_STATUS: LivenessStatusView = deriveLivenessStatus(LIVENESS);

/** Format the snapshot timestamp as `YYYY-MM-DD HH:MMZ` (UTC), or null. */
export function fmtSnapshotTs(generatedAt: string | null): string | null {
  if (!generatedAt) return null;
  return `${generatedAt.slice(0, 16).replace('T', ' ')}Z`;
}
